/*
 * global-carousel-plain.js
 * ------------------------------------------------------------------
 * Registers the PLAIN `<global-carousel>` custom element.
 *
 * GlobalScopedCarousel.js only defines the *scoped* element
 * `global-carousel-<compId>` (read from its own <script data-comp-id>),
 * but it exposes the class globally as `window.GlobalCarousel`.
 *
 * Several ported v2 sections (showcase-slider, multi-panel, single-row,
 * 3-cta-grid, key-point slider) emit a plain `<global-carousel>` and their
 * wrappers (e.g. GlobalShowcaseSlider) do `querySelector("global-carousel")`
 * then `await t.ObserverLite.once()`. medik8.com registers plain
 * `global-carousel` globally in a base bundle that isn't present in this repo;
 * this shim replicates that.
 *
 * IMPORTANT timing detail:
 * The stock GlobalCarousel creates `this.ObserverLite` *inside* its
 * DomReadyPromise().then() callback. If the wrapper element (e.g.
 * <global-showcase-slider>, loaded async per-section) upgrades and runs its
 * own DomReadyPromise callback BEFORE the carousel's, `t.ObserverLite` is
 * still undefined → `t.ObserverLite.once()` throws → the wrapper dies before
 * updateSlides()/buildCtaBlock() (symptom: empty data-distance, blank CTA).
 *
 * Fix: subclass and create ObserverLite EAGERLY at upgrade (in
 * connectedCallback, before DOM-ready) and never overwrite it. Then:
 *   - whichever element upgrades first, `t.ObserverLite` always exists, and
 *   - once() replays via onceDone, so the order of init() vs subscribe()
 *     no longer matters.
 *
 * A custom-element constructor can only be bound to ONE tag name, so the
 * subclass also gives us a fresh constructor for the plain tag.
 *
 * Depends on window.ObserverLite + window.DomReadyPromise (scripts.chunks.core.js)
 * and window.GlobalCarousel (GlobalScopedCarousel.js).
 */
(function () {
  "use strict";

  function define() {
    if (
      !window.customElements ||
      typeof window.GlobalCarousel !== "function" ||
      typeof window.ObserverLite !== "function" ||
      typeof window.DomReadyPromise !== "function"
    ) {
      return false;
    }
    if (customElements.get("global-carousel")) {
      return true;
    }

    var ObserverLite = window.ObserverLite;
    var DomReadyPromise = window.DomReadyPromise;

    try {
      customElements.define(
        "global-carousel",
        class extends window.GlobalCarousel {
          connectedCallback() {
            // Create the observable synchronously at upgrade so any wrapper
            // that grabs this element finds a live `.ObserverLite`, even before
            // DOM-ready. Re-implements the stock connectedCallback minus the
            // (deferred) ObserverLite assignment, so it is never overwritten.
            if (!this.ObserverLite) {
              this.ObserverLite = new ObserverLite();
            }
            DomReadyPromise()
              .then(function () {
                var tpl = this.querySelector(":scope > template");
                if (tpl) {
                  try {
                    this.settings = JSON.parse(tpl.innerHTML);
                  } catch (e) {
                    console.error(e);
                  }
                  tpl.remove();
                  tpl = null;
                }
                this.init();
                // First (uncached) loads can have Embla measure slides before
                // their images have dimensions, leaving the slider mis-sized
                // until a hard refresh (when images are cached). Re-init once
                // everything has loaded so it re-measures. (Wrappers like
                // GlobalShowcaseSlider listen for "reInit" and recompute height.)
                if (document.readyState !== "complete") {
                  window.addEventListener(
                    "load",
                    function () {
                      if (this.embla) {
                        this.embla.reInit();
                      }
                    }.bind(this),
                    { once: true }
                  );
                }
              }.bind(this))
              .catch(function (e) {
                console.error(e);
              });
          }
        }
      );
    } catch (e) {
      // Already defined elsewhere (e.g. a future base bundle) — fine.
    }
    return true;
  }

  // defer keeps us after GlobalScopedCarousel.js, but the plain-carousel
  // sections also async-load their own wrappers, so poll briefly as a fallback.
  if (!define()) {
    var tries = 0;
    var iv = setInterval(function () {
      if (define() || ++tries > 200) {
        clearInterval(iv);
      }
    }, 10);
  }
})();
